import './styles.scss'
import { useState, useMemo } from 'react'
import * as Mui from '@mui/material'
import {
    IconSearch,
    IconChevronDown,
    IconChevronRight,
    IconX,
} from '@tabler/icons-react'
import { Button, Input, Text } from '@fragments'
import { useChatPanel } from './ChatPanelContext'
import { useSidebar } from '../Sidebar/SidebarContext'
import CurrentChat from './CurrentChat'
import Preview from './CurrentChat/components/Preview'

function getInitials(name = '') {
    const parts = name.trim().split(/\s+/)
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
    return (parts[0][0] + (parts[parts.length - 1]?.[0] || '')).toUpperCase()
}

function formatTime(dateStr) {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return ''
    const now = new Date()
    const diffDays = Math.floor((now - date) / 86400000)
    if (diffDays === 0) return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    if (diffDays < 7) {
        const days = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']
        return days[date.getDay()]
    }
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

function getLastMessageText(last_message) {
    if (!last_message) return ''
    if (typeof last_message === 'string') return last_message
    return last_message.text || ''
}

function Avatar({ chat, size = 46 }) {
    return (
        <div className="cp-avatar" style={{ width: size, height: size }}>
            {chat.avatar
                ? <img src={chat.avatar} alt={chat.username} />
                : <span>{getInitials(chat.username || chat.email || '')}</span>
            }
        </div>
    )
}

function ChatRow({ chat, onClick }) {
    const timeStr = formatTime(chat.last_message_at || chat.updated_at)
    const hasUnread = chat.unread_messages > 0

    return (
        <button
            className={`cp-row${hasUnread ? ' cp-row--unread' : ''}`}
            onClick={onClick}
        >
            <div className="cp-row__avatar-wrap">
                <Avatar chat={chat} />
            </div>

            <div className="cp-row__content">
                <div className="cp-row__top">
                    <Text.span contrast={8} className="cp-row__name">
                        {chat.username || chat.email || 'Usuário'}
                    </Text.span>
                    {timeStr && (
                        <Text.span
                            contrast={hasUnread ? 3 : 5}
                            className={`cp-row__time${hasUnread ? ' cp-row__time--unread' : ''}`}
                        >
                            {timeStr}
                        </Text.span>
                    )}
                </div>

                <div className="cp-row__bottom">
                    <Text.span contrast={6} className="cp-row__preview">
                        {getLastMessageText(chat.last_message) || '\u00A0'}
                    </Text.span>
                    {hasUnread && (
                        <span className="cp-unread-badge">
                            {chat.unread_messages > 99 ? '99+' : chat.unread_messages}
                        </span>
                    )}
                </div>
            </div>
        </button>
    )
}

function SkeletonRow() {
    return (
        <div className="cp-skeleton-row">
            <div className="cp-skeleton cp-skeleton--avatar" />
            <div className="cp-skeleton-content">
                <div className="cp-skeleton cp-skeleton--line cp-skeleton--name" />
                <div className="cp-skeleton cp-skeleton--line cp-skeleton--msg" />
            </div>
        </div>
    )
}

export default function ChatPanel() {
    const { open, close, chats, loading, activeChat, openChat, clearActiveChat } = useChatPanel()
    const { isMobile } = useSidebar()
    const [search, setSearch] = useState('')
    const [filter, setFilter] = useState('all')
    const [preview, setPreview] = useState(null)

    const unreadTotal = chats.reduce((acc, c) => acc + (c.unread_messages || 0), 0)

    const filtered = useMemo(() => {
        let list = chats
        if (filter === 'unread') list = list.filter(c => c.unread_messages > 0)
        if (search.trim()) {
            const q = search.toLowerCase()
            list = list.filter(c =>
                c.username?.toLowerCase().includes(q) ||
                c.email?.toLowerCase().includes(q)
            )
        }
        return list
    }, [chats, search, filter])

    return (
        <>
            <div className={`cp-panel${open ? ' cp-panel--open' : ''}${isMobile ? ' cp-panel--mobile' : ''}`}>

                {/* Close tab — floats on the left border, vertically centred (desktop only) */}
                {open && !isMobile && (
                    <Button
                        className="cp-panel__close-tab"
                        icon={<IconChevronRight size={16} strokeWidth={2} />}
                        variant="rounded"
                        color="inverse"
                        size="micro"
                        onClick={close}
                        preventLoading
                    />
                )}

                <div className="cp-panel__inner">

                    {activeChat ? (
                        <div className="cp-chat-view">
                            <CurrentChat
                                info={activeChat}
                                handleOpenPreview={setPreview}
                                onClose={clearActiveChat}
                            />
                        </div>
                    ) : (
                        <>
                            {/* Header */}
                            <div className="cp-panel__header">
                                <Text.h5 contrast={8}>Conversas</Text.h5>
                                <Button
                                    icon={<IconX size={18} strokeWidth={2} />}
                                    variant="rounded"
                                    color="overlay"
                                    size="small"
                                    onClick={close}
                                    tooltip="Fechar"
                                    preventLoading
                                />
                            </div>

                            {/* Search */}
                            <div className="cp-search">
                                <Input.Text
                                    value={search}
                                    onInput={setSearch}
                                    placeholder="Buscar conversa"
                                    start={<IconSearch size={15} strokeWidth={1.8} />}
                                    className="cp-search__input-fragment"
                                />
                            </div>

                            {/* Filters */}
                            <div className="cp-filters">
                                <Button
                                    text="Tudo"
                                    variant="rounded"
                                    size="small"
                                    color={filter === 'all' ? 'primary' : 'inverse'}
                                    onClick={() => setFilter('all')}
                                    preventLoading
                                />
                                <Button
                                    variant="rounded"
                                    size="small"
                                    color={filter === 'unread' ? 'primary' : 'inverse'}
                                    onClick={() => setFilter('unread')}
                                    endIcon={<IconChevronDown size={13} strokeWidth={2} />}
                                    text={`Não lidas${unreadTotal > 0 ? ` ${unreadTotal}` : ''}`}
                                    preventLoading
                                />
                            </div>

                            {/* List */}
                            <div className="cp-list">
                                {loading
                                    ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
                                    : filtered.map(chat => (
                                        <ChatRow
                                            key={chat.id}
                                            chat={chat}
                                            onClick={() => openChat(chat)}
                                        />
                                    ))
                                }
                            </div>
                        </>
                    )}

                </div>
            </div>

            {/* Image preview portal */}
            {preview && (
                <Mui.Portal>
                    <Preview image={preview} onClose={() => setPreview(null)} />
                </Mui.Portal>
            )}
        </>
    )
}
